import {Component, OnInit, inject, TemplateRef, ViewChild, computed, signal, DestroyRef} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {AssetsService} from "../../../services/assets.service";
import {JobsService} from "../../../services/jobs.service";
import {GlobalSettingsService} from "../../../services/global-settings.service";
import {SessionService} from "../../../services/session.service";
import {Permissions} from "../../../enums/permissions";
import {ToastService} from "../../../services/toast.service";
import {AssetTableColumn, constructAssetColumns} from "./assets-table.config";
import {constructRentalColumns, RentalAssetTableColumn} from "./rental-asset-table.config";
import {TableData} from "../../../interfaces/table/table-data";
import {TableParams} from "../../../interfaces/table/table-params";
import {constructDetailsColumns} from "./asset-details-table.config";
import {DateUtils} from "../../../utils/date-utils";
import {auditTime, debounceTime, distinctUntilChanged, Subject, switchMap, tap} from "rxjs";
import {TableAction} from "../../../interfaces/table/table-action";
import {UserStorageService} from "../../../services/user-storage.service";
import {Router} from "@angular/router";
import {FormBuilder, FormGroup} from "@angular/forms";
import {ObjectUtils} from "../../../utils/object-utils";
import {ParamsWrapper} from "../../../interfaces/params-wrapper";

@Component({
  selector: 'app-assets',
  templateUrl: './assets.component.html',
  styleUrls: ['./assets.component.css'],
  standalone: false
})
export class AssetsComponent implements OnInit {
  currentUser: any;
  readonly allLocationsOption = {location_id: 0, location_name: 'All Locations'};
  locations: any[] = [this.allLocationsOption];
  detailsSerialNumber: any;
  isExportDropdownOpen: boolean = false;
  isGeneratingReport: boolean = false;
  currentFileType: 'csv' | 'excel' | null = null;
  activeTab: 'myAssets' | 'rentedAssets' = 'myAssets';
  activationSwitchingAsset: any;
  relocatingAsset: any;
  relocateLocationId: any = null;
  relocateLocations: any[] = [];
  filterForm!: FormGroup;
  serviceTeamEmail = '';

  assetsTableData: TableData = {data: [], total: 0};
  assetsTableColumns = computed(() =>
    constructAssetColumns(this.serialNumberAssetColumn, this.nextPmColumn));
  assetsTableActions: TableAction[] = [];
  loadingAssetTableData = signal<boolean>(true);
  loadAssets$ = new Subject<ParamsWrapper<{ tableParams: TableParams, filterParams: any }>>();
  assetsTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  assetsCurrentPage = signal<number>(1);

  rentalTableData: TableData = {data: [], total: 0};
  rentalTableColumns = computed(() =>
    constructRentalColumns(this.serialNumberRentalColumn));
  loadingRentalTableData = signal<boolean>(true);
  loadRentals$ = new Subject<{ tableParams: TableParams, filterParams: any }>();
  rentalTableParams: TableParams = {currentPage: 1, pageSize: 10, sortParams: null};
  rentalCurrentPage = signal<number>(1);

  detailsTableData = signal<TableData>({data: [], total: 0});
  detailsTableColumns = computed(() => {
    const showDueDate = this.detailsTableData().data.some((job: any) => job.complete_date === '--');
    const showCompleteDate = this.detailsTableData().data.some((job: any) => job.complete_date !== '--');
    return constructDetailsColumns(showDueDate, showCompleteDate);
  });
  loadingDetailsTableData = signal<boolean>(true);
  showDetailsModal = false;
  loadAssetDetails$ = new Subject<string>();

  @ViewChild('serialNumberAssetColumn', {static: true}) serialNumberAssetColumn!: TemplateRef<any>;
  @ViewChild('serialNumberRentalColumn', {static: true}) serialNumberRentalColumn!: TemplateRef<any>;
  @ViewChild('nextPmColumn', {static: true}) nextPmColumn!: TemplateRef<any>;

  protected readonly AssetTableColumn = AssetTableColumn;
  protected readonly RentalAssetTableColumn = RentalAssetTableColumn;
  private readonly assetsService = inject(AssetsService);
  private readonly jobService = inject(JobsService);
  private readonly settingsService = inject(GlobalSettingsService);
  private readonly sessionService = inject(SessionService);
  private readonly userStorageService = inject(UserStorageService);
  private readonly toastService = inject(ToastService);
  private readonly router = inject(Router);
  private readonly fb = inject(FormBuilder);
  private readonly destroyRef = inject(DestroyRef);

  constructor() {
    this.sessionService.currentUser
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(res => this.currentUser = res.user);
  }

  ngOnInit(): void {
    this.initActions();
    this.initFilterForm();
    this.initObservables();
    this.initTab();

    this.refreshData();

    this.jobService.getLocations().then((data: any) => {
      this.locations = [this.allLocationsOption, ...data];
      this.relocateLocations = data;
    });

    this.settingsService.getClientSettings().then((settings: any) => {
      this.serviceTeamEmail = settings?.serviceEmail;
    });
  }

  initActions(): void {
    this.assetsTableActions = [
      {
        displayName: 'Add to Service Request',
        execute: (row) => {
          const result = this.userStorageService.addToStorageServiceRequest({'origin': 'asset', ...row});
          this.toastService.showToast(result.message, result.type);
        },
        textColor: '#3f83f8'
      },
      {
        displayName: 'Move to Location',
        execute: (row) => this.openRelocateModal(row),
        disable: !this.hasPermission(Permissions.RelocateAsset),
        textColor: '#3f83f8'
      },
      {
        displayName: (row) => row.is_active ? 'Deactivate' : 'Activate',
        execute: (row) => this.activationSwitchingAsset = row,
        disable: !this.hasPermission(Permissions.DeactivateAsset),
        textColor: (row) => row.is_active ? '#dc2626' : '#16a34a'
      }
    ];
  }

  initFilterForm() {
    this.filterForm = this.fb.group({
      assetLookup: [''],
      location: [0],
      showInactive: [false]
    });

    this.filterForm.get('assetLookup')!.valueChanges.pipe(
      debounceTime(1000),
      distinctUntilChanged(),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.assetsCurrentPage.set(1);
      this.rentalCurrentPage.set(1);
      this.assetsTableParams = {...this.assetsTableParams, currentPage: 1};
      this.rentalTableParams = {...this.rentalTableParams, currentPage: 1};
      this.refreshData();
    });

    this.filterForm.valueChanges.pipe(
      distinctUntilChanged((prev, curr) =>
        prev.location === curr.location && prev.showInactive === curr.showInactive),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(() => {
      this.assetsCurrentPage.set(1);
      this.rentalCurrentPage.set(1);
      this.assetsTableParams = {...this.assetsTableParams, currentPage: 1};
      this.rentalTableParams = {...this.rentalTableParams, currentPage: 1};
      this.refreshData();
    });
  }

  initObservables() {
    this.loadAssets$.pipe(
      distinctUntilChanged((prev, curr) => {
        if (curr.forceRefresh) return false;
        return ObjectUtils.deepEqual(prev.params, curr.params);
      }),
      tap(_ => this.loadingAssetTableData.set(true)),
      auditTime(0),
      switchMap(value => {
        const {tableParams, filterParams} = value.params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        return this.assetsService.getAssets(tableParams.pageSize, offset, filterParams.assetLookup,
          tableParams.sortParams, filterParams.location, filterParams.showInactive);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      this.loadingAssetTableData.set(false);
      this.assetsTableData = {
        data: response.map((item: any) => ({
          asset_id: item.AssetId,
          serial_number: item.SerialNumber,
          asset_number: item.AssetNumber,
          asset_model: item.ModelName,
          asset_make: item.MakeName,
          asset_type: item.TypeName,
          full_asset_model: `${item.MakeName} ${item.ModelName} ${item.TypeName}`,
          location: item.LocationName,
          last_pm: item.PmFailed
            ? `${DateUtils.formatDate(item.LastPMDate)} (PM Failed)`
            : DateUtils.formatDate(item.LastPMDate),
          next_pm: DateUtils.formatDate(item.NextPMDate),
          next_pm_status_color: this.getStatusColor(item.ServiceStatus),
          last_battery_replacement: DateUtils.formatDate(item.LastBatteryReplacementDate),
          next_battery_replacement: DateUtils.formatDate(item.NextBatteryReplacementDate),
          status: item.ServiceStatus,
          is_active: item.IsActive,
          has_open_jobs: item.HasOpenJobs
        })),
        total: response.length !== 0 ? response[0].TotalCount : 0,
      }
      if (response.length > 0 && response.length === response[0].total_count
        && response.every((item: any) => !item.is_active)) {
        this.filterForm.get('showInactive')!.setValue(true, {emitEvent: false});
      }
    });

    this.loadRentals$.pipe(
      distinctUntilChanged((prev, curr) => ObjectUtils.deepEqual(prev, curr)),
      tap(_ => this.loadingRentalTableData.set(true)),
      auditTime(0),
      switchMap(params => {
        const {tableParams, filterParams} = params;
        const offset = (tableParams.currentPage - 1) * tableParams.pageSize;
        return this.assetsService.getAssetsRental(tableParams.pageSize, offset, filterParams.assetLookup, tableParams.sortParams, filterParams.location);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      this.loadingRentalTableData.set(false);
      this.rentalTableData = {
        data: response.map((item: any) => ({
          asset_id: item.AssetId,
          serial_number: item.SerialNumber,
          asset_number: item.AssetNumber,
          asset_model: item.ModelName,
          asset_make: item.MakeName,
          asset_type: item.TypeName,
          full_asset_model: `${item.MakeName} ${item.ModelName} ${item.TypeName}`,
          location: item.LocationName,
          owner_customer_name: item.OwnerCustomerName,
          location_id: item.LocationId,
          last_pm: DateUtils.formatDate(item.LastPMDate),
          next_pm: DateUtils.formatDate(item.NextPMDate),
          rental_start_date: DateUtils.formatDate(item.RentalStartDate),
          rental_end_date: DateUtils.formatDate(item.RentalEndDate)
        })),
        total: response.length !== 0 ? response[0].TotalCount : 0,
      }
    });

    this.loadAssetDetails$.pipe(
      distinctUntilChanged(),
      switchMap(assetId => {
        this.loadingDetailsTableData.set(true);
        return this.activeTab === 'myAssets'
          ? this.assetsService.getServiceHistory(assetId)
          : this.assetsService.getRentalServiceHistory(assetId);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe((response: any) => {
      this.loadingDetailsTableData.set(false);
      this.detailsTableData.set({
        data: response.map((item: any) => ({
          job_id: item.JobId,
          job_number: item.JobNumber,
          job_type: item.JobTypeName,
          status: {name: item.JobStatusName, color: item.JobStatusColor},
          due_date: DateUtils.formatDate(item.DueDate),
          complete_date: DateUtils.formatDate(item.CompleteDate)
        })),
        total: response.length !== 0 ? response[0].TotalCount : 0,
      });
    });
  }

  initTab() {
    const is_owner = this.currentUser.is_owner;
    const is_rentee = this.currentUser.is_rentee;

    if (is_owner == 1) {
      this.activeTab = 'myAssets'
    }
    if (is_owner == 0 && is_rentee == 1) {
      this.activeTab = 'rentedAssets'
    }
  }

  openAssetDetails(row: any) {
    if (!this.hasPermission(Permissions.ViewAssetDetails)) return;

    this.detailsSerialNumber = row.serial_number;
    this.showDetailsModal = true;

    this.loadAssetDetails$.next(row.asset_id);
  }

  closeAssetDetails() {
    this.showDetailsModal = false;
  }

  hasPermission(permission: string): boolean {
    if (!this.currentUser) return false;
    const permissions = this.currentUser.permission.split(',');
    return permissions.includes(permission);
  }

  getStatusColor(status: string) {
    switch (status) {
      case 'Upcoming':
        return "#ED7D31";
      case 'Past Due':
        return "#C02126";
      default:
        return "#168032";
    }
  }

  onAssetsTableParamChange(params: TableParams) {
    this.assetsTableParams = params;
    this.refreshData();
  }

  onRentalTableParamChange(params: TableParams) {
    this.rentalTableParams = params;
    this.refreshData();
  }

  changeTab(tab: 'rentedAssets' | 'myAssets') {
    this.activeTab = tab;
    this.refreshData();
  }

  refreshData(forceRefresh: boolean = false) {
    const filterParams = {...this.filterForm.value};
    let tableParams: TableParams;
    switch (this.activeTab) {
      case 'myAssets':
        tableParams = {...this.assetsTableParams};
        const params = {tableParams, filterParams};
        this.loadAssets$.next({params, forceRefresh});
        break;
      case 'rentedAssets':
        tableParams = {...this.rentalTableParams};
        this.loadRentals$.next({tableParams, filterParams});
        break;
    }
  }

  isSelectClearable(controlName: string) {
    return this.filterForm.get(controlName)?.value !== 0;
  }

  clearSelectValue(controlName: string) {
    this.filterForm.get(controlName)?.setValue(0);
  }

  toggleExportDropdown(): void {
    this.isExportDropdownOpen = !this.isExportDropdownOpen;
  }

  closeExportDropdown(): void {
    this.isExportDropdownOpen = false;
  }

  handleExport(type: 'asset' | 'rental', format: 'csv' | 'excel'): void {
    if (this.assetsTableData.total === 0) {
      this.toastService.showToast('No Assets were found that match this filter', 'error');
      return;
    }
    this.isExportDropdownOpen = false;
    this.isGeneratingReport = true;
    this.currentFileType = format;
    const filters = this.filterForm.value;

    const apiCallPromise = type === 'asset'
      ? this.assetsService.getAssetsExport(filters.location, filters.assetLookup, format, filters.showInactive ? 1 : 0)
      : this.assetsService.getRentalExport(filters.location, filters.assetLookup, format);

    apiCallPromise
      .then((response: any) => {
        this.isGeneratingReport = false;
        if (response?.fileUrl) {
          window.open(response.fileUrl, '_blank');
        } else {
          this.toastService.showToast('No Assets were exported', 'error');
        }
      })
      .catch(() => {
        this.isGeneratingReport = false;
        this.toastService.showToast('Export failed', 'error');
      });
  }

  onActivationSwitchConfirmed() {
    this.loadingAssetTableData.set(true);
    this.assetsService.UpdateAssetIsActiveStatus(this.activationSwitchingAsset.asset_id, !this.activationSwitchingAsset.is_active)
      .then(() => {
        this.activationSwitchingAsset.is_active = !this.activationSwitchingAsset.is_active;
        const showMessage = this.activationSwitchingAsset.is_active
          ? 'Asset activated successfully' : 'Asset deactivated successfully';
        this.toastService.showToast(showMessage, 'success');
      })
      .catch((error: any) => {
        this.toastService.showToast(error.toString(), 'error');
      })
      .finally(() => {
        this.loadingAssetTableData.set(false);
        this.activationSwitchingAsset = null;
      });
  }

  onActivationSwitchCanceled() {
    this.activationSwitchingAsset = null;
  }

  openRelocateModal(row: any) {
    this.relocatingAsset = row;
    this.relocateLocationId = null;
  }

  onRelocateConfirmed() {
    if (!this.relocateLocationId) {
      this.toastService.showToast('Please select a location', 'error');
      return;
    }

    const assetId = this.relocatingAsset.asset_id;
    this.assetsService.relocateAsset(assetId, this.relocateLocationId)
      .then(() => {
        this.toastService.showToast('Asset relocated successfully', 'success');
        this.refreshData(true);
      })
      .catch((error: any) => {
        this.toastService.showToast(error.toString(), 'error');
      })
      .finally(() => {
        this.relocatingAsset = null;
        this.relocateLocationId = null;
      });
  }

  onRelocateCanceled() {
    this.relocatingAsset = null;
    this.relocateLocationId = null;
  }

  onDetailsRowClick(row: any) {
    this.router.navigate(['/job'], {queryParams: {job_id: row.job_id}});
  }
}
